import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { LoadingStatus, RemoteConfig } from '@qvs/core';
import { environment } from '#environment';
import { DateUtils } from '~/shared/utils/date.utils';
import * as remoteConfigActions from './remoteConfig.actions';

export interface RemoteConfigState {
  remoteConfig: RemoteConfig;
  loadingStatus: LoadingStatus;
}

export const initialState: RemoteConfigState = {
  remoteConfig: {},
  loadingStatus: LoadingStatus.IDLE
};

export type State = RemoteConfigState;

const reducer = createReducer(
  initialState,
  on(remoteConfigActions.loadRemoteConfig, (state) => ({
    ...state,
    loadingStatus: LoadingStatus.LOADING
  })),
  on(remoteConfigActions.loadRemoteConfigSuccess, (state, { response }) => ({
    ...state,
    remoteConfig: response.remoteConfig,
    loadingStatus: LoadingStatus.SUCCEEDED
  })),
  on(remoteConfigActions.loadRemoteConfigFailure, (state) => ({
    ...state,
    loadingStatus: LoadingStatus.FAILED
  }))
);

export const remoteConfigFeatureKey = 'remoteConfig';

export const remoteConfigFeature = createFeature({
  name: remoteConfigFeatureKey,
  reducer
});

const { selectRemoteConfigState } = remoteConfigFeature;

export const selectRemoteConfigLoadingStatus = createSelector(selectRemoteConfigState, (state) => state?.loadingStatus);

export const selectBeforeMaintenanceMessage = createSelector(
  selectRemoteConfigState,
  (state) => state?.remoteConfig?.beforeMaintenanceMessage
);

export const selectMaintenanceMessage = createSelector(
  selectRemoteConfigState,
  (state) => state?.remoteConfig?.maintenanceMessage
);

export const selectIsMaintenanceOngoing = createSelector(selectRemoteConfigState, (state) =>
  shouldDisplayMaintenanceMessage(state.remoteConfig.maintenanceStartDate, state.remoteConfig.maintenanceEndDate)
);

export const selectIsBeforeMaintenanceOngoing = createSelector(selectRemoteConfigState, (state) =>
  shouldDisplayMaintenanceMessage(state.remoteConfig.beforeMaintenanceStartDate, state.remoteConfig.beforeMaintenanceEndDate)
);

const selectCurrentAppVersion = createSelector(selectRemoteConfigState, (state) => state?.remoteConfig?.currentAppVersion);

export const selectMinAppVersion = createSelector(selectRemoteConfigState, (state) => state?.remoteConfig?.minAppVersion);

export const selectIsAppUpdateRequired = createSelector(selectMinAppVersion, (minAppVersion) =>
  isAppVersionHigherThanInstalledVersion(minAppVersion)
);

export const selectIsNewAppVersionAvailable = createSelector(selectCurrentAppVersion, (currentAppVersion) =>
  isAppVersionHigherThanInstalledVersion(currentAppVersion)
);

const isAppVersionHigherThanInstalledVersion = (appVersion: string): boolean => {
  if (!!appVersion && !!environment?.appVersion) {
    const [appVersionNumbers, installedAppVersionNumbers] = [appVersion, environment?.appVersion].map((v) =>
      v.split('.').map(Number)
    );

    for (let i = 0; i < Math.max(appVersionNumbers.length, installedAppVersionNumbers.length); i++) {
      const [appVersionNumber, installedAppVersionNumber] = [appVersionNumbers[i] ?? 0, installedAppVersionNumbers[i] ?? 0];

      if (appVersionNumber !== installedAppVersionNumber) {
        return appVersionNumber > installedAppVersionNumber;
      }
    }
  }
  return false;
};

const shouldDisplayMaintenanceMessage = (maintenanceStartDate: Date, maintenanceEndDate: Date): boolean => {
  if (maintenanceStartDate && maintenanceEndDate) {
    return DateUtils.isCurrentDateBetweenDates(maintenanceStartDate, maintenanceEndDate);
  }

  return false;
};

export const selectCgeNewsToken = createSelector(selectRemoteConfigState, (state) => state?.remoteConfig?.cgeNewsToken);
